import { memo, useCallback, useEffect, useMemo } from 'react';
import { getText } from 'i18n/intl';
import { cloneDeep } from 'lodash';
import type { GeneralInfoProps } from 'pages/configuration/objectGeneration/sections/GeneralInfo';
import { fieldsSelector } from 'slices/field.slice';
import { selectActivityCreateTemplates } from 'slices/objectGenerationTemplate.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import { EFieldType, TType } from '@timeedit/types';
import type {
  TActivityDraftGroupConfig,
  TActivityDraftGroupConfigField,
  TActivityDraftGroupConfigFieldOptionsValuesSchema,
} from '@timeedit/types/lib/types/activityGroup.type';
import {
  Button,
  EditableTable,
  TEDrawerSection,
  TEInput,
} from '@timeedit/ui-components';
import { ArrayOfRules } from '../components/ArrayOfRules';

const allFieldsOnObjectType = (currentObjectType: TType | undefined) => [
  ...(currentObjectType?.parentFields || []),
  ...(currentObjectType?.fields || []),
];

const FieldSection = ({ value, onChangeValue }: GeneralInfoProps) => {
  const allFields = useAppSelector(fieldsSelector);
  const allObjects = useAppSelector(selectObjectTypesList);
  const activityCreateTemplates = useAppSelector(selectActivityCreateTemplates);
  const selectedFields = useMemo(() => {
    const currentObjectTypeId = value.objectType;
    const currentACTId = value.activityCreateTemplateId;
    const activityCreateTemplate = activityCreateTemplates.find(
      (act) => act._id === currentACTId
    );
    const currentObjectType = allObjects.find(
      (obj) => obj.id === currentObjectTypeId
    );
    const activityCreateTemplateFields = activityCreateTemplate?.fields || [];

    const objectTypeFields = allFieldsOnObjectType(currentObjectType);

    return allFields.filter((field) => {
      // These fields cannot be written to in object creation, so we have to exclude them here
      const excludedFieldTypes: EFieldType[] = [
        EFieldType.REFERENCE,
        EFieldType.NON_SEARCHABLE_REFERENCE,
      ];
      if (excludedFieldTypes.includes(field.fieldType as EFieldType)) {
        return false;
      }

      // Include the fields used somewhere (in selected object type or in selected activity create template)
      return (
        objectTypeFields.some((of) => of === field.id) ||
        activityCreateTemplateFields.some((af) => af.extId === field.extId)
      );
    });
  }, [
    allFields,
    value.objectType,
    value.activityCreateTemplateId,
    allObjects,
    activityCreateTemplates,
  ]);
  const objectFields = useMemo(() => {
    const currentObjectTypeId = value.objectType;
    const currentObjectType = allObjects.find(
      (obj) => obj.id === currentObjectTypeId
    );
    const objectTypeFields = allFieldsOnObjectType(currentObjectType);

    const data = allFields.filter((field) => {
      return objectTypeFields.some((of) => of === field.id) && !field.mandatory;
    });
    return data;
  }, [value.objectType, allObjects, allFields]);
  const mandatoryFields = useMemo(() => {
    return selectedFields.filter((f) => f.mandatory);
  }, [selectedFields]);
  const fields = useMemo(() => {
    return value?.fields;
  }, [value]);
  const onSelectRow = useCallback(
    (
      rowIdx: number,
      fieldValue: TActivityDraftGroupConfigFieldOptionsValuesSchema[],
      field: number
    ) => {
      const currentFields = cloneDeep(fields || []);
      if (!currentFields[rowIdx]) {
        currentFields[rowIdx] = { fieldIntId: 0, fillWith: [] };
      }
      currentFields[rowIdx].fillWith = fieldValue;
      currentFields[rowIdx].fieldIntId = field;
      onChangeValue('fields', currentFields);
    },
    [fields, onChangeValue]
  );
  const onDeleteRow = useCallback(
    (rowIdx: number) => {
      const currentFields = [...(fields || [])];
      currentFields.splice(rowIdx, 1);
      onChangeValue('fields', currentFields);
    },
    [fields, onChangeValue]
  );
  const columns = useMemo(
    () => [
      {
        title: getText('field'),
        key: 'id',
        width: 200,
        render: (
          _val: never,
          record: TActivityDraftGroupConfigField,
          idx: number
        ) => {
          const isMandatoryField =
            'fieldIntId' in record &&
            mandatoryFields.find((f) => f.id === record.fieldIntId);
          if (isMandatoryField) {
            return <span>{isMandatoryField.name}</span>;
          }
          return (
            <TEInput.Select
              value={record.fieldIntId || getText('select_field')}
              onChange={(f) => {
                onSelectRow(idx, record.fillWith, f);
              }}
              options={objectFields.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
            />
          );
        },
      },
      {
        title: getText('fill_with'),
        key: 'name',
        width: 200,
        render: (
          _val: never,
          record: TActivityDraftGroupConfigField,
          index: number
        ) => {
          return (
            <ArrayOfRules
              currentTemplate={value}
              currentArrayOfRules={record.fillWith}
              arrayOfRulesChanged={(changedValue) =>
                onSelectRow(index, changedValue, record.fieldIntId)
              }
            ></ArrayOfRules>
          );
        },
      },
    ],
    [value, mandatoryFields, objectFields, onSelectRow, onDeleteRow]
  );

  const dataSource = useMemo(() => {
    return fields;
  }, [fields]);
  useEffect(() => {
    const mandatoryFieldsNotInFields = mandatoryFields.filter((mf) => {
      return !fields?.some((f) => f.fieldIntId === mf.id);
    });
    if (mandatoryFieldsNotInFields.length > 0) {
      const newFields = [...(fields || [])];
      mandatoryFieldsNotInFields.forEach((mf) => {
        newFields.unshift({
          fieldIntId: mf.id,
          fillWith: [],
        });
      });
      onChangeValue('fields', newFields);
      return;
    }
  }, [fields, mandatoryFields]);
  useEffect(() => {
    const currentFieldsInSelectedFields = fields?.filter((f) => {
      return (
        selectedFields.some((sf) => sf.id === f.fieldIntId) ||
        f.fieldIntId === 0
      );
    });

    if (
      currentFieldsInSelectedFields.length &&
      currentFieldsInSelectedFields.length !== (fields || []).length
    ) {
      onChangeValue('fields', currentFieldsInSelectedFields);
    }
  }, [fields, selectedFields, onChangeValue]);
  const onClickAddNew = useCallback(() => {
    const currentFields = [...fields];
    currentFields.push({ fieldIntId: 0, fillWith: [] });
    onChangeValue('fields', currentFields);
  }, [fields, onChangeValue]);

  const footer = useCallback(
    () => (
      <div className="footer-action">
        <Button size="small" type="dashed" onClick={onClickAddNew}>
          {getText('add_field')}
        </Button>
      </div>
    ),
    [onClickAddNew]
  );

  const TableInMemo = memo(
    ({ value }: { value: TActivityDraftGroupConfig }) => {
      return (
        <EditableTable
          columns={columns}
          key={`${value.activityCreateTemplateId}-${
            value.objectType
          }-${mandatoryFields.map((f) => f.id).join('-')}-${objectFields
            .map((f) => f.id)
            .join('-')}`}
          dataSource={dataSource}
          pagination={false}
          virtual={false}
          footer={footer}
        />
      );
    }
  );

  return (
    <TEDrawerSection label={getText('fields')}>
      <div className={'te-mt-2'}>
        <p>{getText('select_field_label')}</p>
      </div>

      <div>
        <TableInMemo value={value} />
      </div>
    </TEDrawerSection>
  );
};

export default FieldSection;
